// Deterministic 2D physics for Cosmic Par.
// Runs on a flat x/z plane (y is purely visual). Fixed timestep = bit-identical
// replay between client and server given the same hole + aim.

export interface Vec2 {
  x: number;
  z: number;
}

export interface Planet {
  /** Orbit center. When `orbit` is unset this is also the render center;
   *  when `orbit` is set the actual body position at sim time t is
   *  `(cx + cos(phase + t*omega)*radius, cz + sin(phase + t*omega)*radius)`. */
  cx: number;
  cz: number;
  radius: number;
  // Effective gravity strength. Already bakes G * mass, so use directly.
  // v6: may be negative — a "repulsor" pushes the ball away instead of
  // pulling. The 1/r² term keeps the same form; the sign of `strength`
  // flips the direction of the resulting acceleration.
  strength: number;
  /** Optional orbit descriptor (v9). When set, the planet moves in a
   *  circle around (cx, cz). Determinism preserved because position is
   *  derived from the sim step time — no RNG, no clock reads. Late-hole
   *  only (e ≥ 0.6). */
  orbit?: {
    radius: number;
    omega: number; // radians per sim-second
    phase: number; // 0..2π
  };
  /** Optional stable identity for visual generation (Cosmic Runner).
   *  When set, `buildPlanet` derives type/colour/spin/tilt from this
   *  seed instead of the shared hole RNG. That keeps a planet visually
   *  identical across multiple `buildHoleVisuals` calls — important
   *  when the runner's sliding-window rebuild changes the planet array
   *  order, which would otherwise re-shuffle every planet's
   *  appearance. Pure cosmetic; physics ignores it. */
  seed?: number;
}

/** Pure helper — planet body position at sim time t (seconds). When no
 *  orbit is set, returns the base (cx, cz). */
export function planetPosAt(p: Planet, t: number): { x: number; z: number } {
  if (!p.orbit) return { x: p.cx, z: p.cz };
  const a = p.orbit.phase + t * p.orbit.omega;
  return {
    x: p.cx + Math.cos(a) * p.orbit.radius,
    z: p.cz + Math.sin(a) * p.orbit.radius,
  };
}

export interface Target {
  cx: number;
  cz: number;
  radius: number;
  /** Optional pulsing target (v9). When set, effective capture radius
   *  oscillates as `radius + sin(phase + t*omega) * amplitude`. Hole
   *  capture check uses the effective radius so timing matters.
   *  amplitude must be < radius so effective radius stays positive. */
  pulse?: {
    amplitude: number;
    omega: number;
    phase: number;
  };
}

/** Effective target radius at sim time t — accounts for pulsing. */
export function targetRadiusAt(tgt: Target, t: number): number {
  if (!tgt.pulse) return tgt.radius;
  return (
    tgt.radius +
    Math.sin(tgt.pulse.phase + t * tgt.pulse.omega) * tgt.pulse.amplitude
  );
}

// Paired wormhole: entering one teleports the ball to its partner with
// preserved velocity. Each wormhole references its partner by index into
// `hole.wormholes` — deterministic and bit-identical across client/server.
export interface Wormhole {
  cx: number;
  cz: number;
  radius: number; // event horizon — ball inside this triggers teleport
  partnerIndex: number;
}

/** Pulsing energy gate — a thin rectangular bar across part of the
 *  arena that strobes between open (pass-through) and closed (bounces).
 *  Cycles: closed for closedSeconds, open for openSeconds. Phase
 *  offset 0..1 within the full cycle. Driven by sim time; no clock reads. */
export interface Gate {
  cx: number; cz: number;        // bar center
  nx: number; nz: number;        // unit normal (perpendicular to bar long axis)
  halfWidth: number;             // along tangent (the long axis)
  thickness: number;             // along normal (~0.6 reasonable)
  closedSeconds: number;
  openSeconds: number;
  phase: number;                 // 0..1
}

/** Comet hazard streak (v11) — a moving hazard that sweeps back and
 *  forth along a straight track. Ball within `radius` of the comet
 *  head at any sim step resets the ball to the tee (treated as
 *  out_of_bounds). Deterministic: position is a pure function of sim
 *  time and the phase/period fields. Late-hole only (e ≥ 0.7). */
export interface Comet {
  /** Track endpoint A. */
  ax: number;
  az: number;
  /** Track endpoint B. */
  bx: number;
  bz: number;
  /** Seconds for a full A→B→A cycle. */
  period: number;
  /** 0..1 starting offset within the cycle. */
  phase: number;
  /** Hit radius (world units) around the comet head. */
  radius: number;
}

/** Pure helper — comet head position at sim time t. Uses a triangle
 *  wave on (t/period + phase) so the comet sweeps A→B→A smoothly
 *  without discontinuities. */
export function cometPosAt(c: Comet, t: number): { x: number; z: number } {
  const period = Math.max(0.0001, c.period);
  const p = (((t / period) + c.phase) % 1 + 1) % 1;
  // Triangle wave: 0..0.5 -> 0..1, 0.5..1 -> 1..0.
  const u = p < 0.5 ? p * 2 : 2 - p * 2;
  return {
    x: c.ax + (c.bx - c.ax) * u,
    z: c.az + (c.bz - c.az) * u,
  };
}

/** Capsule arena segment — a swept circle. Multiple chained segments
 *  define an S-shape or winding track. When `arenaSegments` is set on
 *  HoleLayout, the wall bounce uses distance-to-segment-with-radius
 *  for each capsule (ball is OUT if outside ALL capsules). The
 *  ellipse fields are unused for collision in that case. */
export interface ArenaCapsule {
  ax: number; az: number;
  bx: number; bz: number;
  radius: number;
}

export interface HoleLayout {
  index: number;
  seed: number;
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  /** Circular (or elliptical) arena. When `arenaRadiusX` and
   *  `arenaRadiusZ` are equal the wall is a circle; otherwise it's
   *  an ellipse stretched to those axes. `arenaRadius` is kept as a
   *  fallback shorthand equal to the circular radius. */
  arenaRadius?: number;
  arenaRadiusX?: number;
  arenaRadiusZ?: number;
  tee: Vec2;
  target: Target;
  planets: Planet[];
  wormholes?: Wormhole[];
  /** Constant per-step drift acceleration (units/s²) applied to the
   *  ball every physics tick — "solar wind". Deterministic: just a
   *  constant addition to the integrator, no RNG. Introduced at v6
   *  for late-hole escalation (≥ H7). */
  windAccel?: Vec2;
  /** Pulsing energy gates (v7). Closed bars bounce, open bars pass. */
  gates?: Gate[];
  /** Comet hazards (v11). Moving heads that reset the ball on contact. */
  comets?: Comet[];
  /** Capsule-chain arena (v7). When set, replaces the ellipse for
   *  collision; ball is OUT iff outside ALL capsules. */
  arenaSegments?: ArenaCapsule[];
  /** Multiplier on MAX_POWER for launch velocity (v10). Scales with
   *  arena size so huge endless-mode holes don't turn every shot into
   *  a short hop. Defaults to 1 (vanilla daily behavior).
   *  Deterministic — baked into the layout, applied identically in
   *  simulateShot on client + server. */
  powerScale?: number;
  par: number;
}

/** Pure helper: is this gate currently closed at sim time t (seconds since shot start)? */
export function gateIsClosed(g: Gate, t: number): boolean {
  const period = g.closedSeconds + g.openSeconds;
  // Phase advances forward in time — matches the visual pulse direction.
  const local = ((t / period) + g.phase) % 1;
  const normalized = local < 0 ? local + 1 : local;
  return normalized * period < g.closedSeconds;
}

export interface BallState {
  x: number;
  z: number;
  vx: number;
  vz: number;
}

export interface ShotInput {
  aimX: number; // unit vector component
  aimZ: number;
  power: number; // 0..1
}

export interface ShotSample {
  t: number;
  x: number;
  z: number;
}

export type ShotOutcome =
  | "holed"
  | "rest"
  | "timeout"
  | "out_of_bounds"
  | "comet_hit";

export interface ShotResult {
  outcome: ShotOutcome;
  endState: BallState;
  samples: ShotSample[]; // downsampled trajectory for replay/rendering
  totalSteps: number;
  captureStep: number | null;
}

// ---------- tunables (DO NOT CHANGE MID-SEASON — breaks replays) ----------
export const PHYSICS_DT = 1 / 240; // 240 Hz sim — tight, very stable
export const MAX_POWER = 34; // max launch speed (units/sec)
export const SAMPLE_HZ = 60; // sample trajectory at 60 Hz for render/replay
export const BALL_RADIUS = 0.35;
// REST_SPEED slightly above the original 0.06 so micro-drift at the end
// of a shot doesn't leave the ball creeping for a full second. Keep it
// gentle — too high and shots that should settle right at the hole get
// rejected before capture.
export const REST_SPEED = 0.09;
export const REST_FRAMES = 50; // consecutive frames below REST_SPEED -> rest
// AIR_DAMPING pulled most of the way back toward the original 0.9985.
// Previous 0.997 was too aggressive — slingshot arcs bled speed before
// they could reach the hole. 0.9988 still trims aimless drifts.
export const AIR_DAMPING = 0.9988;
// Planet bounce softened from 0.55 → 0.3 — the ball lost too much
// momentum in repeated 3-4-bounce chains on earlier builds. 0.3 keeps
// a read of "that planet just kicked me" without the pinball carom.
export const PLANET_RESTITUTION = 0.3;
// Wall bounce cut from 0.9 → 0.55 so the ball loses real energy off the
// boundary. Previously a mis-hit sent the ball ping-ponging between
// walls for ages; now it kisses the arc and settles.
export const WALL_RESTITUTION = 0.55;
export const GRAVITY_SOFTENING = 0.35; // r^2 + soft^2 to avoid singularity
export const MAX_ACCEL = 220; // clamp acceleration per step
export const MAX_SIM_SECONDS = 14;
// Hard ceiling on post-bounce speed off a planet (world-units/sec).
// Combined with PLANET_RESTITUTION above, this kills the "pinball for
// 6 bounces before settling" feel on high-speed endless shots. Daily's
// max incoming speed is ≈ 34 * 0.3 = 10.2 after its first bounce,
// well below 15, so the cap never trips on daily.
export const BOUNCE_SPEED_CAP = 15;
// -------------------------------------------------------------------------

const STEPS_PER_SAMPLE = Math.max(1, Math.round((1 / SAMPLE_HZ) / PHYSICS_DT));
const MAX_STEPS = Math.round(MAX_SIM_SECONDS / PHYSICS_DT);

export function launchVelocity(input: ShotInput): { vx: number; vz: number } {
  const len = Math.hypot(input.aimX, input.aimZ) || 1;
  const ux = input.aimX / len;
  const uz = input.aimZ / len;
  const p = clamp01(input.power);
  const speed = p * MAX_POWER;
  return { vx: ux * speed, vz: uz * speed };
}

export function simulateShot(
  hole: HoleLayout,
  startBall: BallState,
  input: ShotInput,
): ShotResult {
  const { vx, vz } = launchVelocity(input);
  const powerScale = hole.powerScale ?? 1;

  // Ball state (copied — keeps caller's state pristine).
  let bx = startBall.x;
  let bz = startBall.z;
  let bvx = vx * powerScale;
  let bvz = vz * powerScale;

  const samples: ShotSample[] = [{ t: 0, x: bx, z: bz }];
  let restFrames = 0;
  let captureStep: number | null = null;
  let outcome: ShotOutcome = "timeout";
  // Wormhole cooldown: after a teleport the ball momentarily sits inside
  // the partner's event horizon until velocity carries it out. Disable
  // wormhole triggers for a short window to prevent a ping-pong loop.
  let wormholeCooldownStep = -1;
  const WORMHOLE_COOLDOWN_STEPS = 30;

  // Solar wind (v6) — constant drift acceleration. Resolved once per
  // shot so the hot inner loop doesn't re-read the optional field. When
  // absent, both components are 0 and the drift is a no-op.
  const windAx = hole.windAccel?.x ?? 0;
  const windAz = hole.windAccel?.z ?? 0;

  for (let step = 1; step <= MAX_STEPS; step++) {
    const simT = step * PHYSICS_DT;
    // ----- gravity integration (semi-implicit Euler — stable for orbits at 240Hz) -----
    let ax = windAx;
    let az = windAz;
    for (let i = 0; i < hole.planets.length; i++) {
      const p = hole.planets[i]!;
      // Moving planets resolve position per step — deterministic because
      // simT is purely derived from the integer step count.
      const ppos = p.orbit ? planetPosAt(p, simT) : null;
      const ppx = ppos ? ppos.x : p.cx;
      const ppz = ppos ? ppos.z : p.cz;
      const dx = ppx - bx;
      const dz = ppz - bz;
      // Repulsors (strength < 0) can otherwise blow up when the ball
      // grazes the surface because the softening alone isn't enough to
      // tame a very-near 1/r². A firmer floor on r² keeps the per-step
      // eject bounded; MAX_ACCEL still caps the total.
      const soft = p.strength < 0
        ? Math.max(GRAVITY_SOFTENING, p.radius * 0.5)
        : GRAVITY_SOFTENING;
      const r2 = dx * dx + dz * dz + soft * soft;
      const r = Math.sqrt(r2);
      const f = p.strength / r2;
      ax += (dx / r) * f;
      az += (dz / r) * f;
    }
    // Clamp acceleration magnitude.
    const amag = Math.hypot(ax, az);
    if (amag > MAX_ACCEL) {
      const k = MAX_ACCEL / amag;
      ax *= k;
      az *= k;
    }

    bvx = (bvx + ax * PHYSICS_DT) * AIR_DAMPING;
    bvz = (bvz + az * PHYSICS_DT) * AIR_DAMPING;
    bx += bvx * PHYSICS_DT;
    bz += bvz * PHYSICS_DT;

    // ----- collisions with planets -----
    for (let i = 0; i < hole.planets.length; i++) {
      const p = hole.planets[i]!;
      const ppos = p.orbit ? planetPosAt(p, simT) : null;
      const ppx = ppos ? ppos.x : p.cx;
      const ppz = ppos ? ppos.z : p.cz;
      const dx = bx - ppx;
      const dz = bz - ppz;
      const minDist = p.radius + BALL_RADIUS;
      const d2 = dx * dx + dz * dz;
      if (d2 < minDist * minDist) {
        const d = Math.sqrt(d2) || 1e-6;
        const nx = dx / d;
        const nz = dz / d;
        // Push out of planet surface.
        const pen = minDist - d;
        bx += nx * pen;
        bz += nz * pen;
        // Reflect velocity. We also cap the post-bounce speed so a
        // ball screaming in at 100+ u/s (endless powerScale=3) can't
        // ricochet off a single planet four or five times. Post-bounce
        // speed = min(incoming * PLANET_RESTITUTION, BOUNCE_SPEED_CAP).
        // Daily incoming speeds max out ≈ 34 * 0.3 = 10.2, well below
        // the 15 cap, so daily feel is unchanged.
        const vdotn = bvx * nx + bvz * nz;
        if (vdotn < 0) {
          const vmag = -vdotn;
          const naiveExit = vmag * PLANET_RESTITUTION;
          const exit = Math.min(naiveExit, BOUNCE_SPEED_CAP);
          const rest = vmag > 1e-4 ? exit / vmag : 0;
          bvx = (bvx - 2 * vdotn * nx) * rest;
          bvz = (bvz - 2 * vdotn * nz) * rest;
        }
      }
    }

    // ----- pulsing gates -----
    // Each gate is a thin rect in (normal, tangent) local space. When
    // closed at this sim time, push the ball out along the nearer face
    // of the expanded rect and reflect the normal-axis velocity.
    if (hole.gates && hole.gates.length > 0) {
      for (let i = 0; i < hole.gates.length; i++) {
        const g = hole.gates[i]!;
        if (!gateIsClosed(g, simT)) continue;
        // Local frame: n = (nx, nz), t = (-nz, nx) (perpendicular).
        const dx = bx - g.cx;
        const dz = bz - g.cz;
        const nDot = dx * g.nx + dz * g.nz;
        const tDot = dx * -g.nz + dz * g.nx;
        const halfN = g.thickness * 0.5 + BALL_RADIUS;
        const halfT = g.halfWidth + BALL_RADIUS;
        if (Math.abs(nDot) < halfN && Math.abs(tDot) < halfT) {
          // Choose the axis with the smaller penetration and push along it.
          const penN = halfN - Math.abs(nDot);
          const penT = halfT - Math.abs(tDot);
          if (penN <= penT) {
            const sign = nDot >= 0 ? 1 : -1;
            bx += g.nx * sign * penN;
            bz += g.nz * sign * penN;
            const outNx = g.nx * sign;
            const outNz = g.nz * sign;
            const vdotn = bvx * outNx + bvz * outNz;
            if (vdotn < 0) {
              bvx = (bvx - 2 * vdotn * outNx) * WALL_RESTITUTION;
              bvz = (bvz - 2 * vdotn * outNz) * WALL_RESTITUTION;
            }
          } else {
            const sign = tDot >= 0 ? 1 : -1;
            // Tangent axis is perpendicular to the normal: (-nz, nx).
            const tx = -g.nz * sign;
            const tz = g.nx * sign;
            bx += tx * penT;
            bz += tz * penT;
            const vdott = bvx * tx + bvz * tz;
            if (vdott < 0) {
              bvx = (bvx - 2 * vdott * tx) * WALL_RESTITUTION;
              bvz = (bvz - 2 * vdott * tz) * WALL_RESTITUTION;
            }
          }
        }
      }
    }

    // ----- wall bounces (play area is bounded) -----
    if (hole.arenaSegments && hole.arenaSegments.length > 0) {
      // Capsule-chain arena: ball is inside the playable region iff
      // some capsule contains it (distance-to-segment ≤ capsule.radius).
      // When outside all, push to the nearest capsule surface and
      // reflect along that outward normal.
      const segs = hole.arenaSegments;
      let bestD = Infinity;
      let bestI = -1;
      let bestCx = 0;
      let bestCz = 0;
      for (let i = 0; i < segs.length; i++) {
        const s = segs[i]!;
        const cp = closestPointOnSegment(bx, bz, s.ax, s.az, s.bx, s.bz);
        const dx = bx - cp.x;
        const dz = bz - cp.z;
        const d = Math.hypot(dx, dz);
        const allow = s.radius - BALL_RADIUS;
        // Positive slack = how far the ball sits inside this capsule's
        // playable interior. We pick the capsule with the greatest slack
        // (most room) so transitions between overlapping capsules are smooth.
        const slack = allow - d;
        if (slack > 0) {
          // Inside this capsule — ball is legal, no correction needed.
          // Track bestD = 0 so the outer "all outside" branch is skipped.
          bestD = 0;
          bestI = -1;
          break;
        }
        const pen = d - allow;
        if (pen < bestD) {
          bestD = pen;
          bestI = i;
          bestCx = cp.x;
          bestCz = cp.z;
        }
      }
      if (bestI >= 0) {
        const s = segs[bestI]!;
        let nx = bx - bestCx;
        let nz = bz - bestCz;
        const nlen = Math.hypot(nx, nz) || 1e-6;
        nx /= nlen;
        nz /= nlen;
        // Snap ball to the capsule surface (inner edge accounting for radius).
        const allow = s.radius - BALL_RADIUS;
        bx = bestCx + nx * allow;
        bz = bestCz + nz * allow;
        const vdotn = bvx * nx + bvz * nz;
        if (vdotn > 0) {
          bvx = (bvx - 2 * vdotn * nx) * WALL_RESTITUTION;
          bvz = (bvz - 2 * vdotn * nz) * WALL_RESTITUTION;
        }
      }
    } else {
      const rX =
        (hole.arenaRadiusX ?? hole.arenaRadius ?? 0) - BALL_RADIUS;
      const rZ =
        (hole.arenaRadiusZ ?? hole.arenaRadius ?? 0) - BALL_RADIUS;
      if (rX > 0 && rZ > 0) {
        // Elliptical (or circular when rX===rZ) arena. The implicit
        // equation is (x/rX)² + (z/rZ)² ≤ 1. On violation, scale the
        // position back to the boundary and reflect velocity across the
        // outward normal (gradient of the implicit function).
        const q = (bx * bx) / (rX * rX) + (bz * bz) / (rZ * rZ);
        if (q > 1) {
          const s = 1 / Math.sqrt(q);
          bx *= s;
          bz *= s;
          // Outward normal: (x/rX², z/rZ²) normalized.
          let nx = bx / (rX * rX);
          let nz = bz / (rZ * rZ);
          const nlen = Math.hypot(nx, nz) || 1e-6;
          nx /= nlen;
          nz /= nlen;
          const vdotn = bvx * nx + bvz * nz;
          if (vdotn > 0) {
            bvx = (bvx - 2 * vdotn * nx) * WALL_RESTITUTION;
            bvz = (bvz - 2 * vdotn * nz) * WALL_RESTITUTION;
          }
        }
      } else {
        if (bx - BALL_RADIUS < hole.bounds.minX) {
          bx = hole.bounds.minX + BALL_RADIUS;
          bvx = Math.abs(bvx) * WALL_RESTITUTION;
        } else if (bx + BALL_RADIUS > hole.bounds.maxX) {
          bx = hole.bounds.maxX - BALL_RADIUS;
          bvx = -Math.abs(bvx) * WALL_RESTITUTION;
        }
        if (bz - BALL_RADIUS < hole.bounds.minZ) {
          bz = hole.bounds.minZ + BALL_RADIUS;
          bvz = Math.abs(bvz) * WALL_RESTITUTION;
        } else if (bz + BALL_RADIUS > hole.bounds.maxZ) {
          bz = hole.bounds.maxZ - BALL_RADIUS;
          bvz = -Math.abs(bvz) * WALL_RESTITUTION;
        }
      }
    }

    // ----- comet hazard streaks -----
    // Moving heads sweep back-and-forth along a straight track. If the
    // ball enters the hit radius, eject the shot as out_of_bounds so
    // the round handler resets to the tee. Cheap — at most a couple of
    // comets per hole, checked once per physics step.
    if (hole.comets && hole.comets.length > 0) {
      for (let i = 0; i < hole.comets.length; i++) {
        const c = hole.comets[i]!;
        const pos = cometPosAt(c, simT);
        const cdx = bx - pos.x;
        const cdz = bz - pos.z;
        const hit = c.radius + BALL_RADIUS;
        if (cdx * cdx + cdz * cdz < hit * hit) {
          outcome = "comet_hit";
          bx = hole.tee.x;
          bz = hole.tee.z;
          bvx = 0;
          bvz = 0;
          break;
        }
      }
      if (outcome === "comet_hit") break;
    }

    // ----- wormhole teleport -----
    // Check each wormhole's event horizon. On entry, jump to the partner
    // and preserve velocity (direction + magnitude). The cooldown prevents
    // the ball from immediately re-triggering on the partner wormhole.
    if (hole.wormholes && hole.wormholes.length > 0 && step > wormholeCooldownStep) {
      for (let i = 0; i < hole.wormholes.length; i++) {
        const w = hole.wormholes[i]!;
        const wdx = bx - w.cx;
        const wdz = bz - w.cz;
        const wd2 = wdx * wdx + wdz * wdz;
        if (wd2 < w.radius * w.radius) {
          const partner = hole.wormholes[w.partnerIndex];
          if (partner) {
            // Offset exit position along the current velocity direction so
            // the ball emerges moving away from the partner's center
            // rather than spawning inside it.
            const speed = Math.hypot(bvx, bvz) || 1;
            const ux = bvx / speed;
            const uz = bvz / speed;
            const exitOffset = partner.radius + BALL_RADIUS + 0.1;
            bx = partner.cx + ux * exitOffset;
            bz = partner.cz + uz * exitOffset;
            wormholeCooldownStep = step + WORMHOLE_COOLDOWN_STEPS;
          }
          break;
        }
      }
    }

    // ----- capture into target -----
    const tdx = bx - hole.target.cx;
    const tdz = bz - hole.target.cz;
    const td = Math.hypot(tdx, tdz);
    // Pulsing targets shrink/grow with sim time; hole.target.radius is
    // the base and targetRadiusAt applies the oscillation. When no pulse
    // is set it returns the base radius, so this is a no-op there.
    const effR = hole.target.pulse
      ? targetRadiusAt(hole.target, simT)
      : hole.target.radius;
    if (td < effR) {
      // Slow, centered balls get captured; fast ones graze past.
      const speed = Math.hypot(bvx, bvz);
      if (speed < 22 || td < effR * 0.35) {
        captureStep = step;
        outcome = "holed";
        break;
      }
    }

    // ----- rest detection -----
    const speed = Math.hypot(bvx, bvz);
    if (speed < REST_SPEED) {
      restFrames++;
      if (restFrames >= REST_FRAMES) {
        outcome = "rest";
        break;
      }
    } else {
      restFrames = 0;
    }

    // ----- sample for replay -----
    if (step % STEPS_PER_SAMPLE === 0) {
      samples.push({ t: step * PHYSICS_DT, x: bx, z: bz });
    }
  }

  // Always record a final sample.
  const lastSample = samples[samples.length - 1]!;
  if (lastSample.x !== bx || lastSample.z !== bz) {
    samples.push({ t: (captureStep ?? MAX_STEPS) * PHYSICS_DT, x: bx, z: bz });
  }

  return {
    outcome,
    endState: { x: bx, z: bz, vx: bvx, vz: bvz },
    samples,
    totalSteps: captureStep ?? MAX_STEPS,
    captureStep,
  };
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

// Closest point on segment AB to P, with t clamped to [0,1].
function closestPointOnSegment(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): { x: number; z: number } {
  const abx = bx - ax;
  const abz = bz - az;
  const len2 = abx * abx + abz * abz;
  if (len2 < 1e-12) return { x: ax, z: az };
  let t = ((px - ax) * abx + (pz - az) * abz) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return { x: ax + abx * t, z: az + abz * t };
}
